import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { Box, Chip, TableCell, TableRow, Tooltip } from '@material-ui/core'
import { ActionButtons } from '../'
import helpers from 'helpers'
import constants from 'constants/index'
import useStyles from './styles'

const ActivitiesTableRow = ({ task, columnMap, onEditCard, onDeleteCard }) => {
  const {
    PRIORITY_LABELS,
    KANBAN_COLUMN_ROLE_PENDING,
    KANBAN_COLUMN_ROLE_FINALIZED,
    KANBAN_COLUMN_ROLE_IN_PROGRESS,
  } = constants.activities

  const classes = useStyles()

  const priorityClass = useMemo(
    () => ({
      severe: classes.prioritySevere,
      high: classes.priorityHigh,
      medium: classes.priorityMedium,
      low: classes.priorityLow,
    }),
    [classes],
  )

  const roleMap = useMemo(
    () =>
      Object.values(columnMap).reduce((acc, col) => {
        acc[col.role] = col
        return acc
      }, {}),
    [columnMap],
  )

  const col = useMemo(() => {
    if (columnMap[task.kanbanColumnId]) return columnMap[task.kanbanColumnId]
    if (!task.targetCompanyId) return undefined
    const EXPLICIT_ROLES = [
      KANBAN_COLUMN_ROLE_PENDING,
      KANBAN_COLUMN_ROLE_FINALIZED,
    ]
    if (EXPLICIT_ROLES.includes(task.role)) return roleMap[task.role]
    return roleMap[KANBAN_COLUMN_ROLE_IN_PROGRESS]
  }, [task, columnMap, roleMap])
  const isOverdue =
    task.dueDate &&
    moment(task.dueDate).isBefore(moment().startOf('day')) &&
    col?.role !== KANBAN_COLUMN_ROLE_FINALIZED

  const responsiblesText = useMemo(() => {
    const full = task.responsibles?.length
      ? task.responsibles.map((r) => r.name).join(', ')
      : null
    return full
  }, [task.responsibles])

  const isReadOnly = Boolean(task.targetCompanyId)

  return (
    <TableRow>
      <TableCell>#{String(task.id).padStart(5, '0')}</TableCell>
      <TableCell>
        <Box display="flex" alignItems="center">
          <Tooltip title={task.name.length > 30 ? task.name : ''}>
            <span>{helpers.truncateUIText(task.name, 30)}</span>
          </Tooltip>
          {task.autoManaged && (
            <Chip
              label="Automática"
              size="small"
              className={classes.autoChip}
            />
          )}
        </Box>
      </TableCell>
      <TableCell>
        {col ? (
          <Tooltip title={col.title.length > 13 ? col.title : ''}>
            <span>
              <Chip
                size="small"
                label={helpers.truncateUIText(col.title, 13)}
                className={classes.columnChip}
                style={{
                  backgroundColor: col.color,
                  color: col.textColor || '#FFFFFF',
                }}
              />
            </span>
          </Tooltip>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        {task.priority ? (
          <span className={priorityClass[task.priority]}>
            {PRIORITY_LABELS[task.priority]}
          </span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        <Box
          display="flex"
          alignItems="center"
          className={classes.responsibleRow}
        >
          {isReadOnly && (
            <Chip
              label="Encarregado (DPO)"
              size="small"
              className={classes.dpoChip}
            />
          )}
          {responsiblesText ? (
            responsiblesText.length > 30 ? (
              <Tooltip title={responsiblesText}>
                <span>{helpers.truncateUIText(responsiblesText, 30)}</span>
              </Tooltip>
            ) : (
              <span>{responsiblesText}</span>
            )
          ) : (
            <span>—</span>
          )}
        </Box>
      </TableCell>
      <TableCell>
        {task.dueDate ? (
          <span className={isOverdue ? classes.deadlineOverdue : undefined}>
            {helpers.formatters.date(task.dueDate)}
          </span>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        {task.createdAt ? helpers.formatters.date(task.createdAt) : '—'}
      </TableCell>
      <TableCell align="right">
        <ActionButtons
          task={task}
          onEdit={onEditCard}
          onDelete={onDeleteCard}
          hideDelete={Boolean(task.targetCompanyId)}
        />
      </TableCell>
    </TableRow>
  )
}

ActivitiesTableRow.propTypes = {
  task: PropTypes.object.isRequired,
  columnMap: PropTypes.object.isRequired,
  onEditCard: PropTypes.func.isRequired,
  onDeleteCard: PropTypes.func.isRequired,
}

export default ActivitiesTableRow
